/**
 * Symbol metadata client — fetches the master symbol library through an
 * injected resource adapter (see the renderer contract in ../README.md) and
 * extracts a keyword set per symbol that acts as its "library entries" for
 * relevance matching, and every symbol's name for the read-outs that show
 * one (a symbol is keyed by its symbol_id; its name is only shown).
 *
 * Used by the Tapestry view to prefer inner-slot clauses that actually
 * link back to the symbol they're being placed inside (e.g. the hand
 * symbol prefers phrases containing words like "hand", "collaboration",
 * "contribution", "creative", etc.).
 */

// Minimal stop-word list for keyword extraction from the symbol's fg.emotion
// / tags / name. Kept short — we just want to drop obvious filler.
const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'of',
  'to',
  'for',
  'in',
  'at',
  'on',
  'by',
  'with',
  'from',
  'as',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'it',
  'its',
  'this',
  'that',
  'these',
  'those',
  'our',
  'your',
  'their',
]);

/**
 * Extract anchor keywords from a single master-symbol entry.
 * Returns a Set<string> of lowercased keywords (≥ 3 chars, non-stop).
 */
function keywordsFromMaster(entry) {
  const out = new Set();
  const add = (raw) => {
    if (!raw) return;
    const lowered = String(raw).toLowerCase();
    for (const word of lowered.split(/[^a-z0-9]+/)) {
      if (word.length >= 3 && !STOP_WORDS.has(word)) {
        out.add(word);
      }
    }
  };

  if (entry.name) add(entry.name);
  if (Array.isArray(entry.tags)) entry.tags.forEach(add);
  if (entry.fg) {
    if (entry.fg.emotion) add(entry.fg.emotion);
    if (Array.isArray(entry.fg.goEmotions)) entry.fg.goEmotions.forEach(add);
  }
  return out;
}

/**
 * @typedef {Object} MasterSymbolResources
 * @property {() => Promise<{symbols: Array, domains: Array}>} loadMasterSymbols
 * @property {() => Promise<Array<{symbol_id: string, name: string}>>} loadSymbols
 */

/**
 * Build a keyword index bound to a resource adapter. The master fetch is
 * memoized per index so the library is loaded once per session; distinct
 * adapters (e.g. a fixture adapter in tests) keep separate caches.
 *
 * @param {MasterSymbolResources} resources
 */
export function createSymbolKeywordIndex(resources) {
  let rawPromise = null; // cached Promise<Array<masterEntry>>
  let keywordPromise = null; // cached Promise<Map<symbol_id, keywordSet>>
  let tagsPromise = null; // cached Promise<Map<symbol_id, string[]>>
  let namesPromise = null; // cached Promise<Map<symbol_id, name>>

  // Single shared master fetch — both the keyword map and the tags map derive
  // from it, so the library is loaded once per index.
  function loadRawMaster() {
    if (rawPromise) return rawPromise;
    rawPromise = (async () => {
      try {
        const data = await resources.loadMasterSymbols();
        return (data && data.symbols) || [];
      } catch (err) {
        console.error('[symbolMeta] loadMasterSymbols failed:', err);
        return [];
      }
    })();
    return rawPromise;
  }

  /**
   * Build a Map<symbol_id, Set<keyword>> that persists for the session.
   */
  function loadSymbolKeywords() {
    if (keywordPromise) return keywordPromise;
    keywordPromise = (async () => {
      const syms = await loadRawMaster();
      const map = new Map();
      for (const entry of syms) map.set(entry.symbol_id, keywordsFromMaster(entry));
      return map;
    })();
    return keywordPromise;
  }

  /**
   * Build a Map<symbol_id, string[]> of master tags — used by the
   * curated layout's `tags` slot rules (fillSlots).
   */
  function loadSymbolTags() {
    if (tagsPromise) return tagsPromise;
    tagsPromise = (async () => {
      const syms = await loadRawMaster();
      const map = new Map();
      for (const entry of syms) map.set(entry.symbol_id, Array.isArray(entry.tags) ? entry.tags : []);
      return map;
    })();
    return tagsPromise;
  }

  /** Map<symbol_id, name> of the whole catalog (`resources.loadSymbols`). */
  function loadSymbolNames() {
    if (namesPromise) return namesPromise;
    namesPromise = (async () => {
      try {
        const syms = await resources.loadSymbols();
        return new Map(syms.map((s) => [s.symbol_id, s.name]));
      } catch (err) {
        console.error('[symbolMeta] loadSymbols failed:', err);
        return new Map();
      }
    })();
    return namesPromise;
  }

  return { loadSymbolKeywords, loadSymbolTags, loadSymbolNames };
}

/**
 * Return true if `text` contains any of the keywords as a word prefix.
 * Uses a 5-character prefix match so "create" catches "creative",
 * "creation", "created", etc.
 */
export function textMatchesKeywords(text, keywords) {
  if (!text || !keywords || keywords.size === 0) return false;
  const lowered = text.toLowerCase();
  for (const kw of keywords) {
    // First try exact word boundary
    const re = new RegExp(`\\b${kw.slice(0, 5)}`, 'i');
    if (re.test(lowered)) return true;
  }
  return false;
}
